//search.component.ts
import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, Subject, BehaviorSubject, of, Observable, combineLatest } from 'rxjs';
import { takeUntil, finalize, distinctUntilChanged, map, startWith, skip } from 'rxjs/operators';
import { ApiService } from '../../services/api.service';
import { ImageCacheService } from '../../services/image-cache.service';
import { BookDTO, JournalDTO, SeriDTO, PersonDTO, ArticleDTO, ChapterDTO, FilterGroup, Filter, SimpleUIObjectDTO } from '../../models/series.model';

@Component({
    selector: 'app-search',
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SearchComponent implements OnInit, OnDestroy {
  term: string = '';
  showFilters: boolean = true;
  type: string = '';

  private filterGroupsSubject = new BehaviorSubject<FilterGroup[]>([]);
  filterGroups$ = this.filterGroupsSubject.asObservable();

  // Index filtresi için arama ve genişletme kontrolleri
  indexSearchTerm: string = '';
  isIndexExpanded: boolean = false;

  get filterGroups(): FilterGroup[] {
    return this.filterGroupsSubject.value;
  }

  dataList: (BookDTO | JournalDTO | SeriDTO | PersonDTO | ArticleDTO | ChapterDTO)[] = [];
  appliedFilters: Filter[] = [];
  isLoading: boolean = false;

  // Expanded titles tracking
  expandedTitles: Set<string> = new Set();
  showTooltips: Set<string> = new Set();

  private routeSubscription?: Subscription;
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private apiService: ApiService,
    private changeDetector: ChangeDetectorRef,
    private imageCacheService: ImageCacheService
  ) {}

  // Çift çağrıları önlemek için flag'ler
  private isInitialLoad = true;
  private isLoadingFilters = false;
  private isLoadingData = false;

  // Protected filter constants
  private readonly PROTECTED_FILTERS = {
    CONFERENCES: '35005100410055006B0063006A0054006D00420073003100',
    ANNIVERSARY_100: '46007400380059004A004F00540034007100740041003100'
  };

  ngOnInit(): void {
    // Dil değişikliğini dinle
    this.translate.onLangChange.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      if (!this.isInitialLoad && this.type) {
        this.reloadDataForLanguageChange();
      }
    });

    // İlk yükleme için sadece snapshot kullan
    const initialParams = this.route.snapshot.params;
    const initialQueryParams = this.route.snapshot.queryParams;

    this.type = initialQueryParams['type'] || initialParams['type'] || 'journal';
    this.term = initialQueryParams['term'] || '';

    // İlk yükleme - sadece bir kez
    this.loadFilters(initialQueryParams);
    this.isInitialLoad = false;

    // Sonraki değişiklikler için route'ları dinle
    this.routeSubscription = combineLatest([
      this.route.params,
      this.route.queryParams
    ]).pipe(
      skip(1), // İlk değeri atla
      distinctUntilChanged((prev, curr) =>
        prev[0]['type'] === curr[0]['type'] &&
        JSON.stringify(prev[1]) === JSON.stringify(curr[1])
      ),
      takeUntil(this.destroy$)
    ).subscribe(([params, queryParams]) => {
      const newType = queryParams['type'] || params['type'] || 'journal';
      const typeChanged = this.type !== newType;
      this.term = queryParams['term'] || '';

      if (typeChanged) {
        this.type = newType;
        this.loadFilters(queryParams);
      } else {
        this.updateAppliedFilters(queryParams);
        this.loadData(queryParams);
      }
    });
  }

  private isProtectedFilter(filter: Filter): boolean {
  const currentQueryParams = this.route.snapshot.queryParams;
  
  // Conference sayfasındaysak ve book_type protected filter ise
  if (currentQueryParams['book_type'] === this.PROTECTED_FILTERS.CONFERENCES) {
    return filter.EID === this.PROTECTED_FILTERS.CONFERENCES && filter.key === 'book_type';
  }
  
  // 100. Yıl sayfasındaysak ve book_type protected filter ise  
  if (currentQueryParams['book_type'] === this.PROTECTED_FILTERS.ANNIVERSARY_100) {
    return filter.EID === this.PROTECTED_FILTERS.ANNIVERSARY_100 && filter.key === 'book_type';
  }
  
  return false;
}

// Protected filter olup olmadığını UI'da göstermek için
isFilterProtected(filter: Filter, groupKey: string): boolean {
  filter.key = groupKey;
  return this.isProtectedFilter(filter);
}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.routeSubscription?.unsubscribe();
    this.filterGroupsSubject.complete();
  }

  toggleFilter(): void {
    this.showFilters = !this.showFilters;
  }

  private reloadDataForLanguageChange(): void {
    const currentQueryParams = this.route.snapshot.queryParams;
    this.loadFilters(currentQueryParams);
  }

  private translateFilterText(originalText: string): string {
    if (!originalText) {
      return '';
    }
    // Çeviri key'ini oluştur - boşlukları kaldır ve @ ekle
    const translationKey = '@' + originalText.replace(/\s+/g, '').replace(/&/g, '&');

    // Çeviri dosyasından bul
    const translatedText = this.translate.instant(translationKey);

    // Eğer çeviri bulunamazsa (key döndürülürse), orijinal metni döndür
    return translatedText !== translationKey ? translatedText : originalText;
  }

  private loadFilters(queryParams: any): void {
    // Eğer zaten filtre yükleme yapılıyorsa, çık
    if (this.isLoadingFilters) {
      return;
    }

    this.isLoadingFilters = true;

    this.apiService.getFilters(this.type)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoadingFilters = false;
        })
      )
      .subscribe({
        next: (loadedFilters: any[]) => {
          loadedFilters.forEach(group => {
            if (group.filters) {
              group.filters.forEach((filter: Filter) => {
                // Çeviri key'ini oluştur ve çevir
                filter.text = this.translateFilterText(filter.text);
              });
            }
          });

          this.filterGroupsSubject.next(loadedFilters);
          this.updateAppliedFilters(queryParams);
          this.changeDetector.detectChanges();

          // Filtreler yüklendikten sonra data'yı yükle
          this.loadData(queryParams);
        },
        error: (err) => {
          this.filterGroupsSubject.next([]);
          this.loadData(queryParams);
        }
      });
  }

  private loadData(queryParams: any): void {
    // Eğer zaten data yükleme yapılıyorsa, çık
    if (this.isLoadingData) {
      return;
    }

    this.isLoadingData = true;
    this.isLoading = true;
    this.expandedTitles.clear();
    this.showTooltips.clear();

    let request$: Observable<any[]>;
    switch (this.type) {
      case 'book':
        request$ = this.apiService.getFilteredBooks(queryParams) as unknown as Observable<any[]>;
        break;
      case 'journal':
        request$ = this.apiService.getFilteredJournals(queryParams) as unknown as Observable<any[]>;
        break;
      case 'article':
        request$ = this.apiService.getFilteredArticles(queryParams) as unknown as Observable<any[]>;
        break;
      case 'chapter':
        request$ = this.apiService.getFilteredChapters(queryParams) as unknown as Observable<any[]>;
        break;
      case 'person':
        request$ = this.apiService.getFilteredPerson(queryParams) as unknown as Observable<any[]>;
        break;
      case 'series':
        request$ = this.apiService.getFilteredSeries(queryParams) as unknown as Observable<any[]>;
        break;
      default:
        request$ = of([] as any[]);
    }

    request$
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
          this.isLoadingData = false;
        })
      )
      .subscribe({
        next: (data: any[]) => {
          this.dataList = data || [];
        },
        error: () => {
          this.dataList = [];
        }
      });
  }

  private hasAppliedFilters(queryParams: any): boolean {
    return this.filterGroups.some(group =>
      queryParams[group.key] && queryParams[group.key].length > 0
    );
  }

  private updateAppliedFilters(queryParams: any): void {
    this.appliedFilters = [];

    const currentFilterGroups = this.filterGroupsSubject.getValue();

    if (!currentFilterGroups || currentFilterGroups.length === 0) {
      return;
    }

    currentFilterGroups.forEach(group => {
      if (!group.filters) {
        return;
      }

      group.filters.forEach(filter => {
        const queryItem = queryParams[group.key];
        if (queryItem) {
          const selectedIds = queryItem.split(',');
          if (selectedIds.indexOf(filter.EID) !== -1) {
            filter.key = group.key;
            this.appliedFilters.push(filter);
          }
        }
      });
    });
  }

  handleFilterChange(group: FilterGroup | undefined, filter: Filter): void {
    if (group) {
      filter.key = group.key;
    }

    // Protected filtreleri değiştirmeye izin verme
    if (this.isProtectedFilter(filter)) {
      return;
    }

    const currentFilterGroups = this.filterGroupsSubject.getValue();
    const queryParams = { ...this.route.snapshot.queryParams };
    const isCurrentlyApplied = this.appliedFilters.some(af => af.EID === filter.EID && af.key === filter.key);

    if (isCurrentlyApplied) {
      this.appliedFilters = this.appliedFilters.filter(af => !(af.EID === filter.EID && af.key === filter.key));
    } else {
      this.appliedFilters.push(filter);
    }

    currentFilterGroups.forEach(g => {
      const groupFilters = this.appliedFilters.filter((f: Filter) => f.key === g.key);
      if (groupFilters.length > 0) {
        queryParams[g.key] = groupFilters.map((f: Filter) => f.EID).join(',');
      } else {
        delete queryParams[g.key];
      }
    });

    this.router.navigate([`/search/${this.type}`], { queryParams });
  }

  onSearch(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      const target = event.target as HTMLInputElement;
      const queryParams = { ...this.route.snapshot.queryParams };

      if (target.value.trim()) {
        queryParams['term'] = target.value.trim();
      } else {
        delete queryParams['term'];
      }

      this.router.navigate([`/search/${this.type}`], { queryParams });
    }
  }

  onTermChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.term = target.value;
  }

  removeFilter(filter: Filter): void {
    // Protected filtreleri kaldırmaya izin verme
    if (this.isProtectedFilter(filter)) {
      return;
    }
    this.handleFilterChange(undefined, filter);
  }

  isFilterSelected(filter: Filter, groupKey: string): boolean {
    return this.appliedFilters.some(af => af.EID === filter.EID && af.key === groupKey);
  }

  getTranslatedText(value: string, keepTranslate?: boolean): string {
    if (!value) {
      return '';
    }
    if (keepTranslate === false) {
      return value;
    }
    return this.translate.instant(value);
  }

  getSeriesImageUrl(series: SeriDTO): string {
    // Direkt URL döndür, cache directive halledicek
    if (series.BannerImage) {
      return `https://cdn.istanbul.edu.tr/image/JTA6CLJ8T5/${series.BannerImage}`;
    }
    
    return '/assets/images/default-series.png';
  }

  truncateDescription(description: string, maxLength: number = 400): string {
    if (description && description.length > maxLength) {
      return `${description.substring(0, maxLength)}...`;
    }
    return description || '';
  }

  hasValidISSN(journal: JournalDTO): boolean {
    return !!(journal.ISSN && journal.ISSN !== '0000-0000') ||
           !!(journal.eISSN && journal.eISSN !== '0000-0000');
  }

  isBook(item: any): item is BookDTO {
    return this.type === 'book';
  }

  isJournal(item: any): item is JournalDTO {
    return this.type === 'journal';
  }

  isArticle(item: any): item is ArticleDTO {
    return this.type === 'article';
  }

  isChapter(item: any): item is ChapterDTO {
    return this.type === 'chapter';
  }

  isPerson(item: any): item is PersonDTO {
    return this.type === 'person';
  }

  isSeries(item: any): item is SeriDTO {
    return this.type === 'series';
  }

  truncateTitle(title: string, maxLength: number = 80): string {
    if (!title) return '';
    if (title.length <= maxLength) return title;

    const truncated = title.substring(0, maxLength);
    const lastSpaceIndex = truncated.lastIndexOf(' ');

    if (lastSpaceIndex > maxLength * 0.8) {
      return truncated.substring(0, lastSpaceIndex) + '...';
    }

    return truncated + '...';
  }

  getFullTitle(title: string): string {
    return title || '';
  }

  shouldTruncateTitle(title: string, maxLength: number = 80): boolean {
    return !!(title && title.length > maxLength);
  }

  toggleTitleExpansion(itemId: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.expandedTitles.has(itemId)) {
      this.expandedTitles.delete(itemId);
    } else {
      this.expandedTitles.add(itemId);
    }
  }

  isTitleExpanded(itemId: string): boolean {
    return this.expandedTitles.has(itemId);
  }

  showTooltip(itemId: string): void {
    this.showTooltips.add(itemId);
  }

  hideTooltip(itemId: string): void {
    this.showTooltips.delete(itemId);
  }

  isTooltipVisible(itemId: string): boolean {
    return this.showTooltips.has(itemId);
  }

  // Index filtresi için yeni metodlar
  getDisplayedIndexFilters(group: FilterGroup): Filter[] {
    if (!group.filters || group.key !== 'index' || this.type !== 'journal') {
      return group.filters || [];
    }

    let filters = group.filters;

    // Arama terimi varsa filtrele
    if (this.indexSearchTerm.trim()) {
      filters = filters.filter(filter => 
        filter.text.toLowerCase().includes(this.indexSearchTerm.toLowerCase())
      );
    }

    // Genişletilmemişse ve arama yoksa ilk 5'ini göster
    if (!this.isIndexExpanded && !this.indexSearchTerm.trim()) {
      return filters.slice(0, 5);
    }

    return filters;
  }

  shouldShowIndexSearch(group: FilterGroup): boolean {
    return this.type === 'journal' && group.key === 'index' && 
           group.filters && group.filters.length > 5;
  }

  shouldShowIndexMore(group: FilterGroup): boolean {
    if (this.type !== 'journal' || group.key !== 'index' || !group.filters) {
      return false;
    }
    
    return group.filters.length > 5 && !this.isIndexExpanded && !this.indexSearchTerm.trim();
  }

  getIndexRemainingCount(group: FilterGroup): number {
    if (this.type !== 'journal' || group.key !== 'index' || !group.filters) {
      return 0;
    }
    return Math.max(0, group.filters.length - 5);
  }

  onIndexSearch(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.indexSearchTerm = target.value;
  }

  toggleIndexExpansion(): void {
    this.isIndexExpanded = !this.isIndexExpanded;
  }

  clearIndexSearch(): void {
    this.indexSearchTerm = '';
    this.isIndexExpanded = false;
  }
}